import React, { useEffect, useRef, useState } from 'react';
import '../styles/hubspot-form.css';
import { leerCampana } from '../lib/campana';

const PORTAL_ID = process.env.REACT_APP_HUBSPOT_PORTAL_ID;
const REGION = process.env.REACT_APP_HUBSPOT_REGION;
const FORM_ID = process.env.REACT_APP_HUBSPOT_FORM_ID;

const SCRIPT_SRC = 'https://js.hsforms.net/forms/embed/v2.js';
const TIMEOUT_MS = 8000;

const WA_NUMERO = '3335593357';
const WA_VISIBLE = '+52 33 3559 3357';
const WA_LINK = `https://wa.me/52${WA_NUMERO}`;

const SERIF = 'var(--soha-serif)';
const SANS = 'var(--soha-sans)';

/** Valores que la propiedad crato_proyecto acepta hoy en HubSpot. Ampliar aquí cuando se creen más. */
export const PROYECTOS_PERMITIDOS = ['DELIA', 'MATILDE', 'CASA_MARA'];

/** Parámetro de campaña (sessionStorage) → campo oculto del formulario. */
const MAPA_CAMPOS = {
  utm_campaign: 'crato_fuente_campana',
  utm_content: 'crato_fuente_conjunto',
  utm_term: 'crato_utm_term',
};

const TEXTOS = {
  es: {
    exitoTitulo: 'Gracias.',
    exitoCuerpo: 'Recibimos tus datos. Un asesor de SOHA Reserve se pondrá en contacto contigo personalmente en menos de 24 horas.',
    exitoWa: 'Si prefieres una conversación inmediata, escríbenos por WhatsApp',
    errorTitulo: 'No pudimos cargar el formulario.',
    errorCuerpo: 'Escríbenos directamente por WhatsApp y te atendemos de inmediato.',
    waCta: 'Escribir por WhatsApp',
  },
  en: {
    exitoTitulo: 'Thank you.',
    exitoCuerpo: 'We received your details. A SOHA Reserve advisor will contact you personally within 24 hours.',
    exitoWa: 'If you prefer an immediate conversation, write to us on WhatsApp',
    errorTitulo: 'We could not load the form.',
    errorCuerpo: 'Write to us directly on WhatsApp and we will assist you right away.',
    waCta: 'Message us on WhatsApp',
  },
};

/** Recolecta nuestras reglas .soha-hs ya compiladas para reinyectarlas dentro del iframe de HubSpot. */
function reglasSoha() {
  const partes = [];
  Array.from(document.styleSheets).forEach((hoja) => {
    let reglas;
    try {
      reglas = hoja.cssRules;
    } catch {
      return; // hoja de otro origen
    }
    Array.from(reglas || []).forEach((regla) => {
      if (regla.cssText && regla.cssText.includes('soha-hs')) partes.push(regla.cssText);
    });
  });
  return partes.join('\n');
}

const FUENTES_IFRAME =
  "@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400&family=Lato:wght@300;400;700&display=swap');";

/**
 * HubSpot puede renderizar el formulario dentro de un iframe (según la configuración
 * del portal). En ese caso reinyectamos los estilos de marca dentro del iframe y
 * sincronizamos su altura.
 */
function estilizarIframe(contenedor) {
  const iframe = contenedor.querySelector('iframe');
  if (!iframe) return null;
  const doc = iframe.contentDocument;
  if (!doc || !doc.body) return null;

  doc.body.classList.add('soha-hs');
  const style = doc.createElement('style');
  style.textContent = `${FUENTES_IFRAME}
    html, body { background: transparent !important; margin: 0; padding: 0; }
    body { color: #F6F2E9; }
    ${reglasSoha()}`;
  doc.head.appendChild(style);

  iframe.style.width = '100%';
  iframe.style.border = '0';
  let ultimaAltura = 0;
  // Medimos el contenido del formulario y escuchamos cambios de DOM (no de tamaño):
  // un ResizeObserver aquí genera el bucle "ResizeObserver loop" al ajustar el iframe.
  const medir = () => {
    const form = doc.querySelector('form');
    return form ? Math.ceil(form.getBoundingClientRect().height) + 16 : doc.body.scrollHeight;
  };
  const sincronizar = () => {
    const alto = medir();
    if (!alto || Math.abs(alto - ultimaAltura) <= 4) return;
    ultimaAltura = alto;
    iframe.style.height = `${alto}px`;
  };
  sincronizar();
  const mutaciones = new MutationObserver(sincronizar);
  mutaciones.observe(doc.body, { subtree: true, childList: true, attributes: true });
  const intervalo = setInterval(sincronizar, 600);
  return {
    doc,
    limpiar: () => {
      mutaciones.disconnect();
      clearInterval(intervalo);
    },
  };
}

/**
 * Escribe un valor en un campo oculto del formulario usando el setter nativo y
 * disparando 'input' con bubbles, para que React y HubSpot registren el cambio.
 */
function setHiddenField(form, name, value) {
  if (!form || !name || !value) return;
  const campo = form.querySelector(`input[name="${name}"], select[name="${name}"], textarea[name="${name}"]`);
  if (!campo) return;

  const vista = campo.ownerDocument?.defaultView || window;
  const proto =
    campo.tagName === 'TEXTAREA' ? vista.HTMLTextAreaElement.prototype : vista.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(campo, value);
  else campo.value = value;

  campo.dispatchEvent(new vista.Event('input', { bubbles: true }));
  campo.dispatchEvent(new vista.Event('change', { bubbles: true }));
}

/** Asegura que cada input visible tenga su label asociado (HubSpot no siempre lo hace). */
function corregirEtiquetas(contenedor) {
  contenedor.querySelectorAll('.hs-form-field').forEach((campo, i) => {
    const label = campo.querySelector(':scope > label');
    const control = campo.querySelector('input:not([type="hidden"]), select, textarea');
    if (!label || !control) return;
    if (!control.id) control.id = `soha-hs-campo-${i}-${control.name || 'input'}`;
    if (!label.getAttribute('for')) label.setAttribute('for', control.id);
    if (control.required) control.setAttribute('aria-required', 'true');
  });
}

/**
 * Formulario nativo de HubSpot (conserva la cookie hubspotutk y la atribución).
 * @param {{ propiedad?: string, lang?: 'es'|'en', className?: string }} props
 */
export default function FormularioHubSpot({ propiedad, lang = 'es', className = '' }) {
  const contenedorRef = useRef(null);
  const idRef = useRef(`soha-hs-form-${Math.random().toString(36).slice(2, 9)}`);
  const [estado, setEstado] = useState('cargando'); // cargando | listo | enviado | error
  const t = TEXTOS[lang] || TEXTOS.es;

  useEffect(() => {
    const faltantes = [];
    if (!PORTAL_ID) faltantes.push('REACT_APP_HUBSPOT_PORTAL_ID');
    if (!FORM_ID) faltantes.push('REACT_APP_HUBSPOT_FORM_ID');
    if (!REGION) faltantes.push('REACT_APP_HUBSPOT_REGION');
    if (faltantes.length) {
      console.warn(
        `[SOHA] Formulario de HubSpot no renderizado. Falta(n) la(s) variable(s) de entorno en frontend/.env: ${faltantes.join(', ')} (Create React App requiere el prefijo REACT_APP_).`
      );
      setEstado('error');
      return undefined;
    }

    const campana = leerCampana();
    const contenedor = contenedorRef.current;
    let cancelado = false;
    let limpiarMarco = null;
    let reintentos = [];

    const temporizador = setTimeout(() => {
      if (!cancelado) setEstado((e) => (e === 'cargando' ? 'error' : e));
    }, TIMEOUT_MS);

    const crear = () => {
      if (cancelado || !contenedor || !contenedor.isConnected) return;
      if (!document.getElementById(idRef.current) || !window.hbspt) return;
      window.hbspt.forms.create({
        region: REGION,
        portalId: PORTAL_ID,
        formId: FORM_ID,
        target: `#${idRef.current}`,
        // Sin estilos de HubSpot: el formulario se inyecta en el DOM (no en iframe)
        // y lo estilizamos nosotros con hubspot-form.css.
        css: '',
        cssRequired: '',
        onFormReady: () => {
          if (cancelado || !contenedor) return;
          const marco = estilizarIframe(contenedor);
          if (marco) limpiarMarco = marco.limpiar;

          // HubSpot puede volver a renderizar sus campos después de onFormReady,
          // así que siempre re-consultamos el documento vivo antes de escribir.
          const docActivo = () => {
            const iframe = contenedor.querySelector('iframe');
            return (iframe && iframe.contentDocument) || contenedor;
          };
          const aplicarValores = () => {
            const doc = docActivo();
            const form = doc.querySelector('form') || doc;
            Object.entries(MAPA_CAMPOS).forEach(([param, name]) => setHiddenField(form, name, campana[param]));
            if (propiedad && PROYECTOS_PERMITIDOS.includes(propiedad)) {
              setHiddenField(form, 'crato_proyecto', propiedad);
            }
            corregirEtiquetas(form);
          };

          if (propiedad && !PROYECTOS_PERMITIDOS.includes(propiedad)) {
            console.warn(
              `[SOHA] crato_proyecto NO se escribió: "${propiedad}" no existe todavía como valor en HubSpot. Permitidos: ${PROYECTOS_PERMITIDOS.join(', ')}. Crea el valor en la propiedad y agrégalo a PROYECTOS_PERMITIDOS.`
            );
          }

          aplicarValores();
          reintentos = [300, 1000, 2500, 5000].map((ms) => setTimeout(aplicarValores, ms));
          // Última garantía: reescribimos justo antes de que el envío salga.
          docActivo().addEventListener('submit', aplicarValores, true);

          clearTimeout(temporizador);
          setEstado('listo');
        },
        onFormSubmitted: () => {
          if (!cancelado) setEstado('enviado');
        },
      });
    };

    const existente = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (window.hbspt) {
      crear();
    } else if (existente) {
      existente.addEventListener('load', crear);
    } else {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.addEventListener('load', crear);
      script.addEventListener('error', () => {
        if (!cancelado) setEstado('error');
      });
      document.body.appendChild(script);
    }

    return () => {
      cancelado = true;
      clearTimeout(temporizador);
      reintentos.forEach(clearTimeout);
      if (limpiarMarco) limpiarMarco();
      if (existente) existente.removeEventListener('load', crear);
      if (contenedor) contenedor.innerHTML = '';
    };
  }, [propiedad]);

  return (
    <div className={`soha-hs ${className}`} data-testid="hubspot-form-wrapper">
      {estado === 'cargando' && <Esqueleto />}

      {estado === 'error' && (
        <div className="py-6" data-testid="hubspot-form-error" role="status">
          <p className="text-2xl md:text-3xl" style={{ fontFamily: SERIF, fontWeight: 300, color: 'var(--soha-marfil)' }}>
            {t.errorTitulo}
          </p>
          <p className="mt-4 text-sm leading-relaxed max-w-sm" style={{ color: 'var(--soha-marfil-82)' }}>
            {t.errorCuerpo}
          </p>
          <BotonWhatsApp t={t} />
        </div>
      )}

      {estado === 'enviado' && (
        <div className="py-6" role="status" aria-live="polite" data-testid="hubspot-form-success">
          <p className="text-3xl md:text-4xl" style={{ fontFamily: SERIF, fontWeight: 300, color: 'var(--soha-marfil)' }}>
            {t.exitoTitulo}
          </p>
          <p className="mt-5 text-base leading-relaxed max-w-sm" style={{ color: 'var(--soha-marfil-82)' }}>
            {t.exitoCuerpo}
          </p>
          <p className="mt-6 text-sm leading-relaxed max-w-sm" style={{ color: 'var(--soha-marfil-45)' }}>
            {t.exitoWa}: <span style={{ color: 'var(--soha-dorado)' }}>{WA_VISIBLE}</span>
          </p>
          <BotonWhatsApp t={t} />
        </div>
      )}

      <div
        id={idRef.current}
        ref={contenedorRef}
        data-testid="hubspot-form-target"
        style={{ display: estado === 'listo' ? 'block' : 'none' }}
      />
    </div>
  );
}

function BotonWhatsApp({ t }) {
  return (
    <a
      href={WA_LINK}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-8 inline-flex items-center justify-center w-full h-14 text-[13px] uppercase transition-colors hover:opacity-85"
      style={{ fontFamily: SANS, letterSpacing: 'var(--soha-track-caption)', border: '1px solid var(--soha-marfil-45)', color: 'var(--soha-marfil)' }}
      data-testid="hubspot-whatsapp-cta"
    >
      {t.waCta}
    </a>
  );
}

function Esqueleto() {
  return (
    <div className="animate-pulse" aria-hidden="true" data-testid="hubspot-form-skeleton">
      <div className="grid gap-7">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={`sk-${i}`}>
            <div className="h-[11px] w-24 mb-3" style={{ background: 'var(--soha-marfil-15)' }} />
            <div className="h-[34px]" style={{ borderBottom: '1px solid var(--soha-marfil-30)' }} />
          </div>
        ))}
        <div className="h-14 mt-2" style={{ background: 'var(--soha-marfil-15)' }} />
      </div>
    </div>
  );
}
